import React, { useEffect, useRef } from "react";
// eslint-disable-next-line no-restricted-imports
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { useFocusRing } from "react-aria";
import { colors, styled } from "../../../stitches.config";
import { ExpandableCellContainer } from "./__styles__/ExpandableCell";
import { PressableTag, PressableTagProps } from "./PressableTag";

export const ExpandableCell = ({
  tags,
  clickable = true,
}: {
  tags?: Array<PressableTagProps>;
  clickable?: boolean;
}) => {
  const [open, setOpen] = React.useState(false);
  const cellRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  let { isFocusVisible, focusProps } = useFocusRing();

  useEffect(() => {
    const handleScroll = (event: Event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  if (!tags) return null;

  const CellButton = () => {
    const handleContainerClick = (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      const tagContainer = target.closest("[data-tag-container]");

      if (tagContainer) {
        // If dropdown is closed, open it on first tag click
        if (!open) {
          if (clickable) {
            setOpen(true);
          }
          return;
        }
        // If dropdown is open, let the tag handle its own click
        return;
      }

      if (clickable) {
        setOpen(true);
      }
    };

    return (
      <ExpandableCellContainer
        onClick={handleContainerClick}
        clickDisabled={!clickable}
        ref={cellRef}
        data-testid="expanding-cell"
        data-focus-visible={isFocusVisible}
      >
        {tags.map((tag, index) => (
          <PressableTag
            key={index}
            label={tag.label}
            iconName={tag.iconName}
            href={tag.href}
            to={tag.to}
            asDropdownItem={false}
            color={tag.color}
            iconColor={tag.iconColor}
            onClick={tag.onClick}
          />
        ))}
      </ExpandableCellContainer>
    );
  };

  return (
    <>
      <CellButton />
      <Dropdown.Root modal={false} open={open} onOpenChange={setOpen}>
        <Dropdown.Trigger asChild tabIndex={0}>
          <div {...focusProps} />
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <DropdownContent
            ref={dropdownRef}
            style={{
              width: cellRef.current?.offsetWidth! + 16,
              top: -cellRef.current?.offsetHeight! - 8,
            }}
            side="bottom"
            sideOffset={-cellRef.current?.offsetHeight! - 8}
            collisionPadding={16}
          >
            <ExpandableCellContainer data-testid="expanded-cell" expanded>
              {tags.map((tag, index) => (
                <PressableTag
                  key={index}
                  label={tag.label}
                  iconName={tag.iconName}
                  href={tag.href}
                  to={tag.to}
                  asDropdownItem={true}
                  onClick={tag.onClick}
                />
              ))}
            </ExpandableCellContainer>
          </DropdownContent>
        </Dropdown.Portal>
      </Dropdown.Root>
    </>
  );
};

// This has to be in the same file and cannot live in the __styles__ file
// Moving to the styles file will break the dropdown
const DropdownContent = styled(Dropdown.Content, {
  background: colors.bgUiSurface,
  border: 1,
  borderColor: colors.borderUiFocused,
  borderStyle: "solid",
  borderRadius: 4,
  zIndex: 2,
});
